import React, { useEffect, useMemo } from 'react';
import { resolveGameIconUrl, POSTS_PER_PAGE } from '../../../utils/gameHelpers';
import './socialFeed.css';

function titleCase(str) {
  return String(str || '')
    .trim()
    .replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

export default function SocialFeed({
  posts,
  loading,
  page,
  onPageChange,
  activeFilter,
  onFilterChange,
  filterOptions,
  votingOpen,
  likingId,
  onLike,
  onDelete,
  onDownload,
  likeBoxBg = '#e25569',
  likeBoxText = '#ffffff',
  textColor = '#000000',
  showCategoryFilter = true,
  variant = 'ecard',
}) {
  const totalPages = Math.max(1, Math.ceil(posts.length / POSTS_PER_PAGE));
  const pagedPosts = useMemo(
    () => posts.slice((page - 1) * POSTS_PER_PAGE, page * POSTS_PER_PAGE),
    [posts, page]
  );

  useEffect(() => {
    if (page > totalPages) onPageChange(Math.max(1, totalPages));
  }, [page, totalPages, onPageChange]);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 1) return [];
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }, [totalPages]);

  const iconUrl = (icon) => resolveGameIconUrl(icon);

  return (
    <div className={`social-feed social-feed--${variant}`} style={{ color: textColor }}>
      {showCategoryFilter && filterOptions?.length > 1 && (
        <div className="social-feed-filter-toolbar">
          <label
            className="social-feed-filter-label"
            htmlFor="social-feed-filter-select"
            style={{ color: textColor }}
          >
            Filter by category
          </label>
          <select
            id="social-feed-filter-select"
            className="social-feed-filter-select"
            value={activeFilter}
            onChange={(e) => onFilterChange(e.target.value)}
            style={{ borderColor: textColor, color: '#222' }}
          >
            {filterOptions.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <p className="social-feed-loading" style={{ color: textColor }}>
          Loading posts…
        </p>
      ) : pagedPosts.length === 0 ? (
        <p className="social-feed-empty" style={{ color: textColor }}>
          No posts yet. Be the first to share!
        </p>
      ) : (
        <div className="social-feed-grid">
          {pagedPosts.map((post) => {
            const dearName = titleCase(post.colleagueName || 'Colleague');
            const fromName = titleCase(post.name || 'Anonymous');
            const mediaSrc = post.uploadFile || post.icon;
            const mediaIsVideo =
              String(post.mediaType || '').startsWith('video') ||
              /\.(mp4|mov)$/i.test(String(mediaSrc || ''));

            return (
              <article
                key={post.id}
                className="social-feed-ecard"
                id={`post-${post.id}`}
                style={{ border: `1px solid ${likeBoxBg}` }}
              >
                <div className="social-feed-ecard-media">
                  {mediaIsVideo && mediaSrc ? (
                    <video
                      src={iconUrl(mediaSrc)}
                      controls
                      playsInline
                      preload="metadata"
                      className="social-feed-ecard-video"
                    />
                  ) : mediaSrc ? (
                    <img
                      src={iconUrl(mediaSrc)}
                      alt={post.ecardName || post.category || ''}
                      className="social-feed-ecard-img"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="social-feed-ecard-placeholder">
                      <span>{post.ecardName || post.category || 'Badge'}</span>
                    </div>
                  )}
                  {post.mine && onDelete ? (
                    <button
                      type="button"
                      className="social-feed-ecard-delete"
                      aria-label="Delete your post"
                      title="Delete"
                      onClick={() => onDelete(post.id)}
                    >
                      &times;
                    </button>
                  ) : null}
                </div>

                <div
                  className="social-feed-ecard-body"
                  style={{
                    backgroundColor: likeBoxBg,
                    color: likeBoxText,
                    borderTop: `1px solid ${likeBoxBg}`,
                  }}
                >
                  <div className="social-feed-ecard-dear">Dear {dearName},</div>
                  {post.message ? (
                    <div className="social-feed-ecard-msg">
                      {String(post.message).slice(0, 200)}
                    </div>
                  ) : null}
                  <div className="social-feed-ecard-footer">
                    <button
                      type="button"
                      className={`social-feed-ecard-like${post.likedByMe ? ' is-liked' : ''}`}
                      style={{ color: likeBoxText }}
                      disabled={!votingOpen || likingId === post.id}
                      onClick={() => onLike?.(post)}
                      aria-label={votingOpen ? 'Like' : 'Likes are disabled'}
                      title={votingOpen ? 'Like' : 'Voting is closed'}
                    >
                      <span className="social-feed-ecard-like-icon" aria-hidden="true">
                        👍
                      </span>
                      <span>{post.likeCount || 0}</span>
                    </button>
                    <span className="social-feed-ecard-from">-From {fromName}</span>
                  </div>
                  {onDownload ? (
                    <button
                      type="button"
                      className="social-feed-ecard-download"
                      style={{ color: likeBoxText }}
                      onClick={() => onDownload(post.id)}
                    >
                      Download
                    </button>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <nav className="social-feed-pagination" aria-label="Posts pagination">
          <button
            type="button"
            className="social-feed-page-btn"
            style={{ borderColor: textColor, color: textColor }}
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            Previous
          </button>
          {pageNumbers.map((n) => (
            <button
              key={n}
              type="button"
              className={`social-feed-page-num${page === n ? ' is-active' : ''}`}
              style={
                page === n
                  ? undefined
                  : { borderColor: textColor, color: textColor }
              }
              onClick={() => onPageChange(n)}
              aria-current={page === n ? 'page' : undefined}
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            className="social-feed-page-btn"
            style={{ borderColor: textColor, color: textColor }}
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </button>
        </nav>
      )}
    </div>
  );
}
